import { useRef, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    RadioGroup,
    Radio,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Chip,
    Stack,
    Button,
    IconButton,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SignatureCanvas from 'react-signature-canvas';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RichEditor from '../Admin/FormBuilder/components/builder/RichEditor';
import FieldFrame, { FieldLabelText } from '../Admin/FormBuilder/components/shared/FieldFrame';
import { getInputSx, getInputVariant, getSubmitButtonSx, getOptionsLayoutSx, getTextStyleSx, getImageContainerSx, getDropzoneSx } from '../Admin/FormBuilder/utils/fieldStyling';
import { isSubfieldEnabled, isSubfieldRequired, applyAutocompleteResult, clearAutocompleteSelection } from '../Admin/FormBuilder/utils/addressGroupFields';
import { normalizeTextListItems } from '../Admin/FormBuilder/utils/textListItems';
import { normalizeConsentItems } from '../Admin/FormBuilder/utils/consentItems';
import { getAcceptAttribute, getAllowedFileTypesLabel } from '../Admin/FormBuilder/utils/fileTypes';
import RoundedCheckbox from '../Admin/FormBuilder/components/shared/RoundedCheckbox';
import { useDebounce } from '../../hooks/useDebounce';
import GeocodingAPI from '../../api/GeocodingAPI';
import { iconButtonSx } from '../Admin/FormBuilder/utils/styles';

// This page can be embedded in an iframe that auto-resizes to fit the form's
// content height (see FormFillPage.jsx's ResizeObserver + the embed
// snippet's postMessage listener). MUI's Select locks body scroll by default
// when its menu opens — toggling `overflow`/`padding-right` on
// document.body — which the ResizeObserver picks up as a (spurious) height
// change and relays to the parent window, which then resizes the iframe and
// visibly jumps the embedding page's scroll position. Disabling the scroll
// lock removes that body mutation at the source; it's a no-op style-wise
// since this page is the only thing in its own document anyway.
const SELECT_MENU_PROPS = { disableScrollLock: true };

// Same trim algorithm the `trim-canvas` package uses (scan for the bounding box of
// non-transparent pixels, then crop to it) — reimplemented locally instead of
// depending on that package, which has a CJS/ESM interop bug under Vite's
// dev-server bundling that throws at runtime (its default export resolves to
// undefined). Mutates and returns the same canvas.
const trimSignatureCanvas = (canvas) => {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);

    const hasInk = (x, y) => data[4 * (y * width + x) + 3] !== 0;

    const findRow = (fromTop) => {
        for (let y = fromTop ? 0 : height - 1; fromTop ? y < height : y > -1; y += fromTop ? 1 : -1) {
            for (let x = 0; x < width; x += 1) {
                if (hasInk(x, y)) return y;
            }
        }
        return null;
    };

    const findCol = (fromLeft) => {
        for (let x = fromLeft ? 0 : width - 1; fromLeft ? x < width : x > -1; x += fromLeft ? 1 : -1) {
            for (let y = 0; y < height; y += 1) {
                if (hasInk(x, y)) return x;
            }
        }
        return null;
    };

    const top = findRow(true);
    const bottom = findRow(false);
    const left = findCol(true);
    const right = findCol(false);

    if (top === null || bottom === null || left === null || right === null) {
        return canvas;
    }

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const imageData = ctx.getImageData(left, top, trimmedWidth, trimmedHeight);

    canvas.width = trimmedWidth;
    canvas.height = trimmedHeight;
    ctx.clearRect(0, 0, trimmedWidth, trimmedHeight);
    ctx.putImageData(imageData, 0, 0);

    return canvas;
};

/**
 * Real, interactive counterpart to the Form Builder canvas's inert
 * FieldRenderer — actual value/onChange wiring, actually typeable/clickable,
 * used on the public /form/{id}/ fill page.
 */
const InteractiveFieldRenderer = ({ field, value, error, onChange, onSubmit, formStyling }) => {
    const signatureRef = useRef(null);
    const { type, label, required, options } = field;

    // Address autocomplete search box — declared unconditionally (matching
    // signatureRef above) since this component instance's field type/switch
    // branch is stable across re-renders, only actually used by 'address-group'.
    const [addressQuery, setAddressQuery] = useState('');
    const [addressOptions, setAddressOptions] = useState([]);
    const [addressLoading, setAddressLoading] = useState(false);
    const debouncedAddressQuery = useDebounce(addressQuery, 400);

    useEffect(() => {
        if (type !== 'address-group' || !field.autoComplete?.enabled) return;
        if (!debouncedAddressQuery || debouncedAddressQuery.trim().length < 3) {
            setAddressOptions([]);
            return;
        }

        let cancelled = false;
        setAddressLoading(true);
        GeocodingAPI.search(debouncedAddressQuery)
            .then((response) => {
                if (!cancelled) setAddressOptions(response?.data ?? []);
            })
            .catch(() => {
                if (!cancelled) setAddressOptions([]);
            })
            .finally(() => {
                if (!cancelled) setAddressLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedAddressQuery, type, field.autoComplete?.enabled]);

    const frameProps = {
        showLabel: formStyling?.showLabel,
        labelAlignment: field.labelAlignment ?? formStyling?.labelAlignment,
        labelWidth: formStyling?.labelWidth,
        labelColor: formStyling?.labelColor,
        labelFontSize: formStyling?.labelFontSize,
        labelFontWeight: formStyling?.labelFontWeight,
        shortLabel: field.shortLabel,
        shortLabelColor: formStyling?.shortLabelColor,
        shortLabelFontSize: formStyling?.shortLabelFontSize,
        shortLabelFontWeight: formStyling?.shortLabelFontWeight,
        errorText: error,
    };
    const variant = getInputVariant(formStyling);
    const inputSx = getInputSx(formStyling);
    // The field's own actual pixel width within the form — options-column
    // capping needs this, not just the form's overall configured width.
    const fieldWidthPx = ((formStyling?.width ?? 640) * (field.width ?? 100)) / 100;

    switch (type) {
        case 'text':
        case 'email':
        case 'number':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <TextField
                        type={type === 'number' ? 'number' : 'text'}
                        placeholder={field.placeholder}
                        value={value ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant={variant}
                        sx={inputSx}
                        error={Boolean(error)}
                        InputProps={
                            type === 'email'
                                ? {
                                      startAdornment: (
                                          <InputAdornment position="start">
                                              <MailOutlineIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                                          </InputAdornment>
                                      ),
                                  }
                                : undefined
                        }
                    />
                </FieldFrame>
            );

        case 'textarea':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <TextField
                        placeholder={field.placeholder}
                        value={value ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        variant={variant}
                        sx={inputSx}
                        error={Boolean(error)}
                    />
                </FieldFrame>
            );

        case 'date':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <DatePicker
                        value={value ?? null}
                        onChange={(newValue) => onChange(newValue)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                placeholder: field.placeholder,
                                variant,
                                sx: inputSx,
                                error: Boolean(error),
                            },
                        }}
                    />
                </FieldFrame>
            );

        case 'file': {
            const allowedFileTypesLabel = getAllowedFileTypesLabel(field);
            const dropzoneSx = getDropzoneSx(formStyling);
            const textColor = formStyling?.inputTextColor || '#1F2937';
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <Box
                        component="label"
                        sx={{
                            ...dropzoneSx,
                            py: 5,
                            px: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1.5,
                            }}
                        >
                            <CloudUploadOutlinedIcon sx={{ color: textColor }} />
                        </Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: textColor }}>
                            {value?.name || field.placeholder || 'Click to upload'}
                        </Typography>
                        {allowedFileTypesLabel && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {allowedFileTypesLabel}
                            </Typography>
                        )}
                        <input
                            type="file"
                            hidden
                            accept={getAcceptAttribute(field)}
                            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
                        />
                    </Box>
                </FieldFrame>
            );
        }

        case 'select':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <FormControl fullWidth size="small" error={Boolean(error)} variant={variant}>
                        <Select
                            value={value ?? ''}
                            displayEmpty
                            onChange={(e) => onChange(e.target.value)}
                            sx={inputSx}
                            MenuProps={SELECT_MENU_PROPS}
                        >
                            <MenuItem value="" disabled>
                                <Typography variant="body2" color="text.secondary">
                                    {field.placeholder ?? 'Choose an option'}
                                </Typography>
                            </MenuItem>
                            {options.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FieldFrame>
            );

        case 'multiselect': {
            const selected = Array.isArray(value) ? value : [];
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <FormControl fullWidth size="small" error={Boolean(error)} variant={variant}>
                        <Select
                            multiple
                            displayEmpty
                            value={selected}
                            onChange={(e) => onChange(e.target.value)}
                            sx={inputSx}
                            MenuProps={SELECT_MENU_PROPS}
                            renderValue={(sel) =>
                                sel.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {field.placeholder ?? 'Choose options'}
                                    </Typography>
                                ) : (
                                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                                        {sel.map((option) => (
                                            <Chip key={option} label={option} size="small" />
                                        ))}
                                    </Stack>
                                )
                            }
                        >
                            {options.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FieldFrame>
            );
        }

        case 'radio':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)} sx={getOptionsLayoutSx(field.optionsLayout, fieldWidthPx)}>
                        {options.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio size="small" />}
                                label={
                                    <FieldLabelText
                                        label={option}
                                        labelColor={frameProps.labelColor}
                                        labelFontSize={frameProps.labelFontSize}
                                        labelFontWeight={frameProps.labelFontWeight}
                                    />
                                }
                            />
                        ))}
                    </RadioGroup>
                </FieldFrame>
            );

        case 'checkbox': {
            const selected = Array.isArray(value) ? value : [];
            const toggle = (option) => {
                onChange(
                    selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option]
                );
            };
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <Box sx={getOptionsLayoutSx(field.optionsLayout, fieldWidthPx)}>
                        {options.map((option) => (
                            <FormControlLabel
                                key={option}
                                control={<Checkbox size="small" checked={selected.includes(option)} onChange={() => toggle(option)} />}
                                label={
                                    <FieldLabelText
                                        label={option}
                                        labelColor={frameProps.labelColor}
                                        labelFontSize={frameProps.labelFontSize}
                                        labelFontWeight={frameProps.labelFontWeight}
                                    />
                                }
                            />
                        ))}
                    </Box>
                </FieldFrame>
            );
        }

        case 'address-group': {
            const groupFields = field.fields ?? {};
            const addr = value ?? {};
            // A manual edit after a suggestion was selected un-selects it, so
            // "mandatory" correctly re-requires picking a (possibly new) one.
            const updateAddr = (key, val) => onChange(clearAutocompleteSelection({ ...addr, [key]: val }));
            const streetSubfield = groupFields.street;
            const wrappedKeys = ['city', 'state', 'country', 'postal'].filter((key) =>
                isSubfieldEnabled(groupFields[key])
            );
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <Stack spacing={1.5}>
                        {field.autoComplete?.enabled && (
                            <Autocomplete
                                freeSolo
                                filterOptions={(opts) => opts}
                                options={addressOptions}
                                loading={addressLoading}
                                inputValue={addressQuery}
                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                                onInputChange={(e, newValue) => setAddressQuery(newValue)}
                                onChange={(e, selected) => {
                                    if (selected && typeof selected !== 'string') {
                                        onChange(applyAutocompleteResult(addr, selected));
                                        setAddressQuery(selected.label);
                                        setAddressOptions([]);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search address"
                                        size="small"
                                        variant={variant}
                                        sx={inputSx}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <SearchIcon fontSize="small" sx={{ ml: 1, mr: 0.5, color: 'text.disabled' }} />
                                            ),
                                            endAdornment: (
                                                <>
                                                    {addressLoading ? <CircularProgress size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                        {isSubfieldEnabled(streetSubfield) && (
                            <FieldFrame
                                label={streetSubfield?.label ?? 'Street Address'}
                                required={isSubfieldRequired(streetSubfield, field, 'street')}
                                labelAlignment="top"
                                showLabel
                                labelColor={frameProps.labelColor}
                                labelFontSize={frameProps.labelFontSize}
                                labelFontWeight={frameProps.labelFontWeight}
                            >
                                <TextField
                                    placeholder={streetSubfield?.placeholder}
                                    value={addr.street ?? ''}
                                    onChange={(e) => updateAddr('street', e.target.value)}
                                    fullWidth
                                    size="small"
                                    variant={variant}
                                    sx={inputSx}
                                />
                            </FieldFrame>
                        )}
                        {wrappedKeys.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {wrappedKeys.map((key) => {
                                    const subfield = groupFields[key];
                                    const defaultLabel = key === 'postal' ? 'Postal Code' : key[0].toUpperCase() + key.slice(1);
                                    return (
                                        <Box key={key} sx={{ flex: '1 1 45%' }}>
                                            <FieldFrame
                                                label={subfield?.label ?? defaultLabel}
                                                required={isSubfieldRequired(subfield, field, key)}
                                                labelAlignment="top"
                                                showLabel
                                                labelColor={frameProps.labelColor}
                                                labelFontSize={frameProps.labelFontSize}
                                                labelFontWeight={frameProps.labelFontWeight}
                                            >
                                                <TextField
                                                    placeholder={subfield?.placeholder}
                                                    value={addr[key] ?? ''}
                                                    onChange={(e) => updateAddr(key, e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    variant={variant}
                                                    sx={inputSx}
                                                />
                                            </FieldFrame>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Stack>
                </FieldFrame>
            );
        }

        case 'signature':
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, backgroundColor: '#FAFAFA', position: 'relative' }}>
                        <SignatureCanvas
                            ref={signatureRef}
                            // No fixed width/height here on purpose: react-signature-canvas only
                            // recalculates the canvas's internal pixel resolution (and the
                            // devicePixelRatio scale factor it draws with) when canvasProps has NO
                            // explicit width/height — passing them (as this used to) disables that
                            // entirely, so the canvas's drawing coordinate space (whatever fixed
                            // number was set) and its actual rendered CSS size (responsive, "100%")
                            // silently drifted apart, offsetting every stroke from the real cursor
                            // position — worse the more the embed's actual width differed from that
                            // hardcoded number. Letting the library measure the real offsetWidth/
                            // offsetHeight itself keeps ink aligned with the pointer at any width.
                            canvasProps={{ style: { width: '100%', height: 140 } }}
                            // Not getTrimmedCanvas() — it depends on the `trim-canvas` package, which has a
                            // CJS/ESM interop bug under Vite's dev-server bundling (its default export
                            // resolves to undefined at runtime). Copy the live canvas first — trimming in
                            // place would corrupt the widget's own drawing surface — then trim the copy
                            // with our own reimplementation of the same algorithm.
                            onEnd={() => {
                                const liveCanvas = signatureRef.current?.getCanvas();
                                if (!liveCanvas) return;
                                const copy = document.createElement('canvas');
                                copy.width = liveCanvas.width;
                                copy.height = liveCanvas.height;
                                copy.getContext('2d').drawImage(liveCanvas, 0, 0);
                                trimSignatureCanvas(copy).toBlob((blob) => onChange(blob), 'image/png');
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => {
                                signatureRef.current?.clear();
                                onChange(null);
                            }}
                            sx={{ position: 'absolute', top: 4, right: 4, ...iconButtonSx }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </FieldFrame>
            );

        case 'consent': {
            const items = normalizeConsentItems(field);
            const checked = Array.isArray(value) ? value : [];
            const toggleItem = (index, isChecked) => {
                const next = items.map((_, i) => Boolean(checked[i]));
                next[index] = isChecked;
                onChange(next);
            };
            return (
                <Box>
                    <Stack spacing={1.5}>
                        {items.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <RoundedCheckbox
                                    checked={Boolean(checked[index])}
                                    onChange={(e) => toggleItem(index, e.target.checked)}
                                    color={formStyling?.primaryColor || '#3F4926'}
                                />
                                <Box sx={{ pt: 0.75, flex: 1, '& .ProseMirror p': { m: 0 } }}>
                                    <RichEditor value={item.content ?? '<p></p>'} readOnly />
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                    {error && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            );
        }

        case 'richtext':
            return (
                <Box sx={getTextStyleSx(field.textStyle)}>
                    <RichEditor value={field.content ?? '<p></p>'} readOnly />
                </Box>
            );

        case 'image':
            if (!field.image?.src) {
                return (
                    <Box
                        sx={{
                            border: '1px solid #E5E7EB',
                            borderRadius: 1,
                            backgroundColor: '#F9FAFB',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                            color: '#9CA3AF',
                        }}
                    >
                        <ImageOutlinedIcon sx={{ fontSize: 32, mb: 1 }} />
                        <Typography variant="body2">Image placeholder</Typography>
                    </Box>
                );
            }
            return (
                <Box sx={getImageContainerSx(field.image?.alignment)}>
                    <img
                        src={field.image.src}
                        alt={field.image.alt ?? ''}
                        style={{
                            width: field.image?.width ? `${field.image.width}px` : 'auto',
                            height: field.image?.height ? `${field.image.height}px` : 'auto',
                            maxWidth: '100%',
                            borderRadius: 4,
                        }}
                    />
                </Box>
            );

        case 'text-list': {
            const currentItems = normalizeTextListItems(Array.isArray(value) ? value : field.items);
            const updateItem = (index, newValue) => {
                onChange(currentItems.map((item, i) => (i === index ? { ...item, value: newValue } : item)));
            };
            return (
                <FieldFrame label={label} required={required} {...frameProps}>
                    <Stack spacing={1.5}>
                        {currentItems.map((item, index) => (
                            <FieldFrame
                                key={index}
                                label={item.label}
                                labelAlignment="left"
                                showLabel
                                labelColor={frameProps.labelColor}
                                labelFontSize={frameProps.labelFontSize}
                                labelFontWeight={frameProps.labelFontWeight}
                            >
                                <TextField
                                    value={item.value}
                                    onChange={(e) => updateItem(index, e.target.value)}
                                    placeholder="Add values"
                                    fullWidth
                                    size="small"
                                    variant={variant}
                                    sx={inputSx}
                                    error={Boolean(error)}
                                />
                            </FieldFrame>
                        ))}
                    </Stack>
                </FieldFrame>
            );
        }

        case 'button': {
            const isButtonFullWidth = field.buttonStyle?.fullWidth ?? true;
            const submitButton = (
                <Button
                    fullWidth={isButtonFullWidth}
                    variant="contained"
                    onClick={onSubmit}
                    sx={getSubmitButtonSx(field.buttonStyle, formStyling)}
                >
                    {label}
                </Button>
            );
            return isButtonFullWidth ? (
                submitButton
            ) : (
                <Box sx={getImageContainerSx(field.buttonStyle?.alignment)}>{submitButton}</Box>
            );
        }

        default:
            return null;
    }
};

export default InteractiveFieldRenderer;
